import React from 'react';

import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';

interface BusinessImpactSectionProps {
  className?: string;
}

const BusinessImpactSection: React.FC<BusinessImpactSectionProps> = ({ className = '' }) => {
  const impactMetrics = [
  {
    icon: 'ArrowTrendingUpIcon',
    metric: 'Overall Revenue',
    description: 'Total revenue generated across all channels',
    stat: '$123 Million',
    color: 'from-success to-trust'
  },
  {
    icon: 'UserGroupIcon',
    metric: 'Increase in Conversion Rate in 6 Months',
    description: 'Growth multiplier in conversion performance',
    stat: '2.35 X Growth',
    color: 'from-primary to-accent'
  },
  {
    icon: 'ChartBarIcon',
    metric: 'Cart/dropout Conversions Achieved',
    description: 'Successfully recovered abandoned cart conversions',
    stat: '9%',
    color: 'from-conversion to-warning'
  }];


  const commercialDrivers = [
  {
    title: 'Sell More',
    description: 'AI-powered engagement identifies upsell opportunities and accelerates deal closure across every customer touchpoint.',
    icon: 'CurrencyDollarIcon',
    image: "/assets/images/sell-more-1770798793350.png",
    alt: 'Professional business team analyzing growth charts and sales data on modern computer displays'
  },
  {
    title: 'Retain More',
    description: 'Predictive intelligence detects at-risk customers before they churn, enabling proactive retention strategies that work.',
    icon: 'ShieldCheckIcon',
    image: "/assets/images/retain-more-1770798936650.png",
    alt: 'Business professionals collaborating on customer retention strategies with positive data trends'
  },
  {
    title: 'Grow Value Per Customer',
    description: 'Coordinated interactions across channels compound over time, turning one-time buyers into high-value, loyal advocates.',
    icon: 'ArrowPathIcon',
    image: "/assets/images/grow-as-customer-value-1770799041337.png",
    alt: 'Upward trending business growth visualization showing customer value increase over time'
  }];

  const outcomes = [
  { label: 'Higher Conversions', icon: 'ArrowTrendingUpIcon' },
  { label: 'Increase repeat purchases', icon: 'ArrowPathRoundedSquareIcon' },
  { label: 'Grow customer lifetime value', icon: 'ChartBarIcon' },
  { label: 'Scale predictable revenue', icon: 'CurrencyDollarIcon' }];


  return (
    <section className={`relative pt-6 pb-12 bg-gradient-to-br from-background to-surface ${className}`}>
      <div className="container mx-auto px-4">
        <div className="max-w-6xl mx-auto">
          {/* D2C Growth Section - MOVED TO TOP */}
          <div className="bg-gradient-to-br from-primary/10 to-accent/10 rounded-2xl p-8 lg:p-12 border border-primary/20 mb-16">
            <div className="text-center mb-10">
              <h3 className="font-display font-bold text-2xl lg:text-4xl text-foreground mb-4">
                Accelerating D2C Growth
              </h3>
              <p className="font-body text-lg lg:text-xl text-muted-foreground leading-relaxed max-w-3xl mx-auto">UnifyMax optimizes the entire customer lifecycle from acquisition to lifetime value.

              </p>
            </div>

            {/* Business Outcomes for D2C Brands */}
            <div className="mb-10">
              
              {/* Outcomes Grid */}
            </div>

            {/* How UnifyMax Drives D2C Growth */}
            <div className="mt-12">
              <h4 className="font-display font-bold text-xl lg:text-2xl text-foreground mb-8 text-center">
                How UnifyMax <span className="text-primary">Drives D2C Growth</span>
              </h4>
              
              <div className="grid md:grid-cols-2 gap-6">
                {/* Box 1 */}
                <div className="bg-white rounded-xl p-6 shadow-md flex items-center space-x-4 hover:shadow-lg transition-shadow duration-300">
                  <div className="bg-gradient-to-br from-primary to-accent rounded-full p-3 flex-shrink-0">
                    <Icon name="CursorArrowRaysIcon" size={24} variant="solid" className="text-white" />
                  </div>
                  <p className="font-body text-base text-foreground leading-relaxed">
                    Converts <span className="font-semibold">high-intent website visitors</span> in real time
                  </p>
                </div>

                {/* Box 2 */}
                <div className="bg-white rounded-xl p-6 shadow-md flex items-center space-x-4 hover:shadow-lg transition-shadow duration-300">
                  <div className="bg-gradient-to-br from-success to-trust rounded-full p-3 flex-shrink-0">
                    <Icon name="DevicePhoneMobileIcon" size={24} variant="solid" className="text-white" />
                  </div>
                  <p className="font-body text-base text-foreground leading-relaxed">
                    Creates <span className="font-semibold">phygital, trust-led buying experiences</span> at scale
                  </p>
                </div>

                {/* Box 3 */}
                <div className="bg-white rounded-xl p-6 shadow-md flex items-center space-x-4 hover:shadow-lg transition-shadow duration-300">
                  <div className="bg-gradient-to-br from-conversion to-warning rounded-full p-3 flex-shrink-0">
                    <Icon name="UserGroupIcon" size={24} variant="solid" className="text-white" />
                  </div>
                  <p className="font-body text-base text-foreground leading-relaxed">
                    Unifies <span className="font-semibold">sales, support, and advisory</span> into one CX system
                  </p>
                </div>

                {/* Box 4 */}
                <div className="bg-white rounded-xl p-6 shadow-md flex items-center space-x-4 hover:shadow-lg transition-shadow duration-300">
                  <div className="bg-gradient-to-br from-primary to-accent rounded-full p-3 flex-shrink-0">
                    <Icon name="SparklesIcon" size={24} variant="solid" className="text-white" />
                  </div>
                  <p className="font-body text-base text-foreground leading-relaxed">
                    Builds a <span className="font-semibold">long-term engagement channel</span> beyond the first purchase
                  </p>
                </div>
              </div>
            </div>

            {/* Closing Statement */}
            <div className="text-center mt-12">
              <div className="bg-primary from-blue-50 to-indigo-50 border-2 border-primary rounded-2xl p-8 lg:p-12 shadow-lg">
                <p className="font-heading text-xl lg:text-2xl font-bold text-white leading-relaxed">
                  Because when systems work in isolation, outcomes stay fragmented.
                  <br />
                  When they work together, growth becomes inevitable.
                </p>
              </div>
            </div>
          </div>

          {/* Main Section Header - Commercial Performance */}
          <div className="text-center mb-12 py-6 px-4">
            <div className="inline-flex items-center space-x-2 bg-conversion/10 rounded-full px-4 py-2 mb-6">
              <Icon name="CurrencyDollarIcon" size={20} variant="solid" className="text-conversion" />
              <span className="font-body text-sm font-medium text-conversion">Commercial Performance</span>
            </div>
            <h2 className="font-display font-bold text-3xl lg:text-5xl text-foreground mb-6 leading-tight">
              This directly impacts your topline.
            </h2>
            <p className="font-body text-lg lg:text-xl text-muted-foreground leading-relaxed max-w-4xl mx-auto mb-4">
              If customer engagement doesn't increase revenue or lifetime value, it doesn't matter.
            </p>
            <p className="font-display font-semibold text-xl lg:text-2xl text-foreground">
              UnifyMax is built for commercial outcomes, not vanity CX metrics.
            </p>
          </div>

          {/* Impact Metrics Cards */}
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {impactMetrics.map((metric, index) =>
            <div
              key={index}
              className="bg-card rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2 border border-border">

                <div className={`bg-gradient-to-br ${metric.color} rounded-xl p-3 inline-block mb-4`}>
                  <Icon name={metric.icon as any} size={28} variant="solid" className="text-white" />
                </div>
                <div className="font-display font-bold text-4xl text-foreground mb-2 whitespace-nowrap">{metric.stat}</div>
                <h3 className="font-display font-bold text-lg text-foreground mb-2 leading-snug">{metric.metric}</h3>
                <p className="font-body text-xs text-muted-foreground leading-relaxed">{metric.description}</p>
              </div>
            )}
          </div>

          {/* Commercial Drivers */}
          <div className="mb-16">
            <div className="text-center mb-12">
              <h3 className="font-display font-bold text-2xl lg:text-4xl text-foreground mb-4">
                Three Ways UnifyMax Drives Revenue Growth
              </h3>
              <p className="font-body text-lg text-muted-foreground max-w-3xl mx-auto">
                Every feature, every integration, every AI model is designed to move one of these three commercial levers.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {commercialDrivers.map((driver, index) =>
              <div
                key={index}
                className="bg-card rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 border border-border flex flex-col">

                  {/* Image Section - Enhanced for better visibility */}
                  <div className="relative h-64 w-full overflow-hidden bg-gray-100">
                    <AppImage
                    src={driver.image}
                    alt={driver.alt}
                    width={400}
                    height={256}
                    className="w-full h-full object-cover" />

                    <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-black/20"></div>
                  </div>

                  {/* Content Section */}
                  <div className="p-6 flex flex-col flex-grow">
                    <div className="mb-4">
                     {/*  <div className="bg-gradient-to-br from-primary to-accent rounded-full p-3 inline-block">
                        <Icon name={driver.icon as any} size={24} variant="solid" className="text-white" /> 
                      </div>*/}
                    </div>
                    <h4 className="font-display font-bold text-xl lg:text-2xl text-foreground mb-3">
                      {driver.title}
                    </h4>
                    <p className="font-body text-sm lg:text-base text-muted-foreground leading-relaxed">
                      {driver.description}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>);

};

export default BusinessImpactSection;